package net.frozenbit.games.soundgame.utils

import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.transformLatest
import kotlinx.coroutines.isActive
import net.frozenbit.games.soundgame.dtos.WsTimeout
import kotlin.math.round
import kotlin.time.Clock
import kotlin.time.Duration
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.seconds
import kotlin.time.Instant

internal class TimeCorrection(
    private val clock: Clock = Clock.System,
) {
    private var differenceToRemote: Duration? = null

    /**
     * Sets the remote time to serve as a reference for time correction.
     * Use the [ignoreIfInitialized] parameter to stabilize the time correction.
     * When set to `true`, the current time correction is kept, if the class
     * has already been initialized.
     */
    fun calibrate(remoteTime: Instant, ignoreIfInitialized: Boolean = true) {
        if (ignoreIfInitialized && differenceToRemote != null) {
            return
        }
        val localTime = clock.now()
        differenceToRemote = remoteTime - localTime
    }

    fun actual(theirs: Instant): Instant {
        val difference = differenceToRemote
            ?: return theirs
        return theirs - difference
    }
}

class Countdown(
    private val clock: Clock = Clock.System,
    private val format: (Duration?) -> String = { duration ->
        if (duration == null) {
            "-"
        } else {
            val remainingSeconds = duration.inWholeSeconds
            when {
                remainingSeconds < 10 -> "0$remainingSeconds"
                else -> "$remainingSeconds"
            }
        }
    },
) {
    private var targetTime = MutableStateFlow<Pair<Instant, Instant>?>(null)

    fun start(
        start: Instant = clock.now(),
        target: Instant,
    ) {
        targetTime.value = target to start
    }

    fun clear() {
        targetTime.value = null
    }

    data class Tick(
        val text: String,
        /**
         * A value between 0 and 1 indicating the progress of the countdown.
         * 0 means the countdown has just started, and 1 means the countdown has finished
         */
        val progress: Float,
        val remaining: Duration,
        val startTime: Instant,
        val endTime: Instant,
    )

    fun tickFlow(): Flow<Tick?> =
        targetTime.transformLatest { target ->
            if (target == null) {
                emit(null)
                return@transformLatest
            }
            val (end, start) = target
            while (currentCoroutineContext().isActive) {
                val now = clock.now()
                val remaining = end - now

                if (remaining.isNegative()) {
                    emit(
                        Tick(
                            text = format(Duration.ZERO),
                            progress = 1f,
                            remaining = Duration.ZERO,
                            startTime = start,
                            endTime = end,
                        )
                    )
                    break
                }
                val roundedToSeconds = round(remaining.inWholeMilliseconds / 1000.0).seconds
                emit(
                    Tick(
                        text = format(roundedToSeconds),
                        progress = 1f - (remaining / (end - start)).toFloat(),
                        remaining = remaining,
                        startTime = start,
                        endTime = end,
                    )
                )

                val remainingMillis = remaining.inWholeMilliseconds % 1000
                delay(
                    when {
                        remainingMillis < 100 -> 1.seconds
                        // try to align with the next second boundary
                        else -> remainingMillis.milliseconds
                    }
                )
            }
        }
}

data class Timeout(
    val start: Instant,
    val end: Instant,
) {
    companion object {
        internal fun from(
            dto: WsTimeout,
            timeCorrection: TimeCorrection,
        ): Timeout {
            return Timeout(
                start = timeCorrection.actual(dto.start),
                end = timeCorrection.actual(dto.end),
            )
        }
    }
}
